import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { storableError } from '../../util/errors';
import { getStartOf, stringifyDateToISO8601 } from '../../util/dates';
import { parse } from '../../util/urlHelpers';
import { getSupportedProcessesInfo } from '../../transactions/transaction';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';

import { FALLBACK_TIME_ZONE } from '../InstructorDiaryPage/InstructorDiaryPage.duck';
import { diaryWeekStart } from '../InstructorDiaryPage/InstructorDiaryPage.helpers';

// Lessons in one week fit in one page: 100 is the Marketplace API maximum.
const ORDERS_PAGE_SIZE = 100;

// Learners have no listing to read a time zone from. Drivelo runs in Melbourne, so the
// learner's week is shown in Melbourne time, the same fallback the instructor diary uses.
export const LEARNER_TIME_ZONE = FALLBACK_TIME_ZONE;

const entityRefs = entities => entities.map(entity => ({ id: entity.id, type: entity.type }));

const learnerDiaryPageSlice = createSlice({
  name: 'LearnerDiaryPage',
  initialState: {
    fetchInProgress: false,
    fetchError: null,
    weekStartId: null,
    transactionRefs: [],
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(loadDataThunk.pending, state => {
        state.fetchInProgress = true;
        state.fetchError = null;
      })
      .addCase(loadDataThunk.fulfilled, (state, action) => {
        const { weekStartId, transactions } = action.payload;
        state.fetchInProgress = false;
        state.weekStartId = weekStartId;
        state.transactionRefs = entityRefs(transactions);
      })
      .addCase(loadDataThunk.rejected, (state, action) => {
        console.error(action.payload || action.error);
        state.fetchInProgress = false;
        state.fetchError = action.payload;
      });
  },
});

export default learnerDiaryPageSlice.reducer;

// ================ Load data ================ //

// Read-only: the learner's own bookings (orders) that start in the week shown.
const loadDataPayloadCreator = ({ search }, { dispatch, rejectWithValue, extra: sdk }) => {
  const { date } = parse(search || '');
  const timeZone = LEARNER_TIME_ZONE;
  const weekStart = diaryWeekStart(date, timeZone);
  const weekEnd = getStartOf(weekStart, 'day', timeZone, 7, 'days');

  const ordersQueryParams = {
    only: 'order',
    processNames: getSupportedProcessesInfo().map(p => p.name),
    // START,END in ISO 8601: bookings that start in this week.
    bookingStart: `${weekStart.toISOString()},${weekEnd.toISOString()}`,
    include: ['listing', 'provider', 'booking'],
    'fields.transaction': [
      'processName',
      'lastTransition',
      'lastTransitionedAt',
      'transitions',
      // Pickup address and lesson focus from checkout; only the two parties can read it.
      'protectedData',
    ],
    'fields.listing': ['title'],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'deleted', 'banned'],
    page: 1,
    perPage: ORDERS_PAGE_SIZE,
  };

  return sdk.transactions
    .query(ordersQueryParams)
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      return {
        weekStartId: stringifyDateToISO8601(weekStart, timeZone),
        transactions: response.data.data,
      };
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const loadDataThunk = createAsyncThunk('LearnerDiaryPage/loadData', loadDataPayloadCreator);

export const loadData = (params, search) => dispatch => dispatch(loadDataThunk({ params, search }));
